import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

function Dashboard() {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState("7days");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(
      collection(db, "users", user.uid, "transactions"),
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTransactions(data);
      }
    );

    return () => unsub();
  }, [user]);

  const now = new Date();

  const filtered = transactions.filter((t) => {
    const date = new Date(t.createdAt.seconds * 1000);

    if (filter === "today") {
      return (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      );
    }

    if (filter === "7days" || filter === "week") {
      return now - date <= 7 * 24 * 60 * 60 * 1000;
    }

    if (filter === "month") {
      return (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      );
    }

    if (filter === "year") {
      return date.getFullYear() === now.getFullYear();
    }

    return true;
  });

  const total = filtered.reduce((sum, t) => sum + t.amount, 0);

  const categoryMap = {};
  filtered.forEach((t) => {
    categoryMap[t.category] =
      (categoryMap[t.category] || 0) + t.amount;
  });

  const pieData = Object.keys(categoryMap).map((key) => ({
    name: key,
    value: categoryMap[key]
  }));

  const dailyMap = {};
  filtered.forEach((t) => {
    const day = new Date(t.createdAt.seconds * 1000)
      .toDateString()
      .slice(0, 10);

    dailyMap[day] = (dailyMap[day] || 0) + t.amount;
  });

  let barData = Object.keys(dailyMap).map((day) => ({
    day,
    amount: dailyMap[day]
  }));

  barData.sort((a, b) => new Date(a.day) - new Date(b.day));

  let topCategory = "";
  let max = 0;

  Object.entries(categoryMap).forEach(([cat, amt]) => {
    if (amt > max) {
      max = amt;
      topCategory = cat;
    }
  });

  return (
    <div className="w-full">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-white">Dashboard</h2>
          <p className="text-slate-400 text-sm">Track your spending insights</p>
        </div>

        <div className="bg-[#111827]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl">

          {/* Filters */}
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            {["today", "7days", "week", "month", "year"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                ${filter === f
                    ? "bg-green-500 text-white shadow-lg scale-105"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600 hover:scale-105"
                  }`}
              >
                {f === "7days" ? "7 Days" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

            {/* Total */}
            <div className="bg-gradient-to-br from-green-500/20 to-green-700/10 border border-green-400/20 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase mb-1">Total Spending</p>
              <p className="text-3xl font-bold text-white">
                ₹{total.toLocaleString()}
              </p>
            </div>

            {/* Top Category */}
            {topCategory && (
              <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase mb-1">
                  Top Category
                </p>
                <p className="text-xl font-bold text-white">{topCategory}</p>
                <p className="text-sm text-green-400">
                  ₹{max.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Pie */}
            {pieData.length > 0 && (
              <div className="bg-slate-900/80 rounded-xl p-4 shadow-inner border border-slate-700">
                <h3 className="text-center text-slate-300 text-sm font-semibold mb-3">
                  Category Breakdown
                </h3>

                <div className="flex justify-center">
                  <PieChart width={260} height={240}>
                    <Pie data={pieData} dataKey="value" outerRadius={75}>
                      {pieData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}  // ✅ FIXED
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        background: "#020617",
                        border: "none",
                        borderRadius: "8px",
                        color: "#F8FAFC",
                        fontSize: "12px",
                      }}
                    />
                  </PieChart>
                </div>
              </div>
            )}

            {/* Bar */}
            {barData.length > 0 && (
              <div className="bg-slate-900/80 rounded-xl p-4 shadow-inner border border-slate-700">
                <h3 className="text-center text-slate-300 text-sm font-semibold mb-3">
                  Spending Trend
                </h3>

                <div className="flex justify-center">
                  <BarChart width={300} height={240} data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="day" stroke="#94A3B8" fontSize={10} />
                    <YAxis stroke="#94A3B8" fontSize={10} />
                    <Tooltip
                      contentStyle={{
                        background: "#020617",
                        border: "none",
                        borderRadius: "8px",
                        color: "#F8FAFC",
                        fontSize: "12px",
                      }}
                    />
                    <Bar dataKey="amount" fill="#22C55E" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </div>
              </div>
            )}
          </div>

          {/* Empty */}
          {pieData.length === 0 && (
            <div className="text-center py-10 text-slate-500 text-sm">
              🚫 No transactions in this period
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
